import { AuthState, useAuthState } from '@dtx-company/inter-app/src/hooks/useAuthState'
import { AvailablePlan } from '@dtx-company/inter-app/src/types/AvailablePlan'
import { AvailablePlanExtra } from '@dtx-company/inter-app/src/types/AvailablePlanExtra'
import { Dispatch, SetStateAction, createContext, useMemo, useState } from 'react'
import { PPEPromoCodeState, usePPEPromoCodeState } from './hooks/usePPEPromoCodeState'
import { Plan, PlanPurchaseExperienceMode } from '@dtx-company/true-common/src/types/Plan'
import { PlanPurchaseExperienceProps } from './types'
import { Subscription } from '@dtx-company/inter-app/src/types/subscription'
import { UseSubscriptionResponse } from '@dtx-company/inter-app/src/hooks/useCurrentSubscriptionSWR'
import { useAvailablePlanExtrasSWR } from '../../hooks/useAvailablePlanExtrasSWR'
import { useAvailablePlansSWR } from '../../hooks/useAvailablePlansSWR'
import { useFlowFeature } from '@dtx-company/inter-app/src/hooks/useFlowFeature/useFlowFeature'
import { usePlanPurchaseExperiencePlans } from './hooks/usePlanPurchaseExperiencePlans'
import { usePlanPurchaseExperiencePricingGroup } from '@app/common/src/components/PlanPurchaseExperience/hooks/usePlanPurchaseExperiencePricingGroup'
import { usePlanPurchaseExperienceSubscriptionInfo } from '@app/common/src/components/PlanPurchaseExperience/hooks/usePlanPurchaseExperienceSubscriptionInfo'
import { useUpgradePageQuery } from '../../../../../components/pages/UpgradePage/useUpgradePageQuery'

export interface PlanPurchaseExperienceContextProps {
  authState: AuthState
  plans: Plan[] | undefined
  selectedPlan: Plan | undefined
  setSelectedPlan: Dispatch<SetStateAction<Plan | undefined>>
  selectedAvailablePlan: AvailablePlan | undefined
  availablePlans: AvailablePlan[] | undefined
  availablePlanExtrasForSelectedPlan: AvailablePlanExtra[] | undefined
  currentSubscriptionInfoSWR: UseSubscriptionResponse
  currentSubscriptionInfoData: Subscription | null | undefined
  allowPromoCode: boolean
  planCostSummaryV2Enabled: boolean
  /** Whether the user is changing their billing cycle */
  isSwitchingBillingCycle: boolean
  /** State from usePPEPromoCodeState hook */
  promoCodeState: PPEPromoCodeState
  customHeadingText: string | undefined
}

export const PlanPurchaseExperienceContext = createContext<PlanPurchaseExperienceContextProps>(
  {} as PlanPurchaseExperienceContextProps
)

export function usePlanPurchaseExperienceContext(
  props: PlanPurchaseExperienceProps
): PlanPurchaseExperienceContextProps {
  const authState = useAuthState()
  const { currentSubscriptionInfoSWR, currentSubscriptionInfoData } =
    usePlanPurchaseExperienceSubscriptionInfo()
  const [planCostSummaryV2Enabled] = useFlowFeature('plan_cost_summary_v2')
  const plans = usePlanPurchaseExperiencePlans(props)
  const [selectedPlan, setSelectedPlan] = useState<Plan | undefined>(undefined)
  const planPricingGroup = usePlanPurchaseExperiencePricingGroup(props)
  const availablePlansSWR = useAvailablePlansSWR(
    () => {
      if (!authState.token) return
      if (!planPricingGroup) return
      return {
        token: authState.token,
        filter: {
          planPricingGroup
        },
        include: {
          plan: true
        }
      }
    },
    {
      revalidateIfStale: false,
      revalidateOnFocus: false,
      revalidateOnMount: true
    }
  )
  const selectedAvailablePlan = useMemo(() => {
    if (!selectedPlan) return
    if (!availablePlansSWR.data) return
    return availablePlansSWR.data.find((availablePlan: { planId: string }) => {
      return availablePlan.planId === selectedPlan.id
    })
  }, [selectedPlan, availablePlansSWR.data])
  const availablePlanExtrasSWR = useAvailablePlanExtrasSWR(
    () => {
      if (!authState.token) return
      if (!selectedAvailablePlan) return
      return {
        token: authState.token,
        availablePlanId: selectedAvailablePlan.availablePlanId,
        include: {
          planExtra: true
        }
      }
    },
    {
      revalidateIfStale: false,
      revalidateOnFocus: false,
      revalidateOnMount: true
    }
  )
  const allowPromoCode =
    props.mode !== PlanPurchaseExperienceMode.CHANGE_PAYMENT &&
    props.mode !== PlanPurchaseExperienceMode.PURCHASE_ADDONS
  const { mode } = useUpgradePageQuery()

  // If the user is on the PPE with a planType in the query that matches their current subscritpion
  // plan type and they are not in any `mode` it means they want to change their billing cycle
  const isSwitchingBillingCycle = Boolean(
    props.planType && props.planType === currentSubscriptionInfoData?.plan.type && !mode
  )

  const promoCodeState = usePPEPromoCodeState({ selectedPlan })

  const customHeadingText = props.customHeadingText

  return useMemo<PlanPurchaseExperienceContextProps>(() => {
    return {
      authState,
      plans,
      selectedPlan,
      setSelectedPlan,
      selectedAvailablePlan,
      availablePlans: availablePlansSWR.data,
      availablePlanExtrasForSelectedPlan: availablePlanExtrasSWR.data,
      currentSubscriptionInfoSWR,
      currentSubscriptionInfoData,
      allowPromoCode,
      planCostSummaryV2Enabled,
      isSwitchingBillingCycle,
      promoCodeState,
      customHeadingText
    }
  }, [
    authState,
    plans,
    selectedPlan,
    setSelectedPlan,
    selectedAvailablePlan,
    availablePlansSWR.data,
    availablePlanExtrasSWR.data,
    currentSubscriptionInfoSWR,
    currentSubscriptionInfoData,
    allowPromoCode,
    planCostSummaryV2Enabled,
    isSwitchingBillingCycle,
    promoCodeState,
    customHeadingText
  ])
}
